export const CG_BASE = 'https://api.coingecko.com/api/v3';

// The Basic/Pro plan serves a ~2-year historical window. The pro-api market_chart
// endpoint rejects days='max', so callers request this explicit bound instead
// (729 = just under the 2-year boundary, to avoid a boundary/off-by-one error).
export const CG_MAX_DAYS = 729;

// Production routes every CoinGecko call through our Vercel serverless proxy
// (/api/coingecko), which holds the Demo API key server-side and caches at the
// edge. Local dev (npm start) doesn't serve /api, so it falls back to CoinGecko's
// keyless public API directly — fine for single-developer use. The API key is
// NEVER referenced in client code; it lives only in the Vercel env var.
const CG_PROXY = '/api/coingecko';

function isLocalDev(): boolean {
  if (typeof window !== 'undefined' && window.location) {
    const h = window.location.hostname;
    if (h === 'localhost' || h === '127.0.0.1' || h === '0.0.0.0') return true;
    if (h === '' && window.location.protocol === 'file:') return true;
  }
  return process.env.NODE_ENV !== 'production';
}

const FETCH_OPTS: RequestInit = {
  method: 'GET',
  headers: {
    'Accept': 'application/json',
  },
  mode: 'cors',
};

export const RATE_LIMIT_MSG =
  'Price data temporarily unavailable. CoinGecko API rate limit reached. Please try again in a moment.';

function sleep(ms: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

/** Returns a user-friendly error message for common CoinGecko API errors. */
export function cgErrorMsg(err: unknown): string {
  if (err instanceof Error) {
    const status = (err as { response?: { status?: number } }).response?.status;
    if (status === 429) return RATE_LIMIT_MSG;
    if (status === 403)
      return 'API temporarily unavailable. Please try again in a moment.';
    if (err.name === 'AbortError' || err.message?.includes('timeout'))
      return 'Request timed out. The server took too long to respond. Please try again.';
  }
  return 'Failed to load price data. Please try again.';
}

type CgConfig = {
  params?: Record<string, string | number | boolean>;
  timeout?: number;
};

// Build the request URL: proxy in production, public API directly in local dev.
function buildUrl(path: string, params?: CgConfig['params']): string {
  if (isLocalDev()) {
    const url = new URL(`${CG_BASE}${path}`);
    if (params) {
      Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, String(v)));
    }
    return url.toString();
  }
  const origin = (typeof window !== 'undefined' && window.location)
    ? window.location.origin
    : '';
  const url = new URL(CG_PROXY, origin || 'http://localhost');
  url.searchParams.set('endpoint', path);
  if (params) {
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, String(v)));
  }
  // Drop the origin for a relative request when we have one.
  return origin ? url.pathname + url.search : url.toString();
}

async function cgFetch(url: string, timeout: number): Promise<{ data: unknown }> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);

  try {
    const res = await fetch(url, { ...FETCH_OPTS, signal: controller.signal });
    if (!res.ok) {
      const err = Object.assign(new Error(`Request failed with status code ${res.status}`), {
        response: { status: res.status },
      });
      throw err;
    }
    const data = await res.json();
    return { data };
  } finally {
    clearTimeout(timer);
  }
}

// Client-side last-good cache (per session) — a second safety net on top of the
// proxy's edge cache + server fallback, so a transient failure shows the last
// successfully loaded data instead of a blank error.
const lastGood = new Map<string, unknown>();

/**
 * Wrapper around fetch for CoinGecko endpoints.
 * - Routes through the serverless proxy in production, public API in local dev.
 * - Configurable timeout (default 10s).
 * - Retries once after a 2-second delay on any failure.
 * - On final failure, returns the last successful response for the same request
 *   if one exists; otherwise throws so callers can show a Retry option.
 */
export async function cgGet(path: string, config?: CgConfig): Promise<{ data: unknown }> {
  const urlStr = buildUrl(path, config?.params);
  const timeout = config?.timeout ?? 10_000;
  // Stable session cache key (independent of dev/prod URL shape).
  const cacheKey = `${path}|${config?.params ? JSON.stringify(config.params) : ''}`;

  let result: { data: unknown };
  try {
    result = await cgFetch(urlStr, timeout);
  } catch {
    try {
      await sleep(2_000);
      result = await cgFetch(urlStr, timeout);
    } catch (err) {
      const cached = lastGood.get(cacheKey);
      if (cached !== undefined) return { data: cached };
      throw err;
    }
  }

  lastGood.set(cacheKey, result.data);
  return result;
}
